import { type AnalyticsEvent, trackAnalyticsEvent } from '@autone/analytics';
import { type Column } from '@tanstack/react-table';
import { ArrowDown, ArrowDownUp } from 'lucide-react';
import { type PropsWithChildren } from 'react';

import { Button } from '../Button';
import { cn } from '../utils';

interface DataTableHeaderCellWithSortingProps<T, V> extends PropsWithChildren {
  arrowPosition?: 'left' | 'right';
  showSortable?: boolean;
  column: Column<T, V>;
  sortAnalyticsEvent?: AnalyticsEvent;
}

/**
 * SortableHeader renders a button with the arrow next to it showing
 * whether the current column is sorted in desc or asc order
 */
export function DataTableHeaderCellWithSorting<T, V>({
  arrowPosition = 'right',
  showSortable = false,
  column,
  children,
  sortAnalyticsEvent,
}: DataTableHeaderCellWithSortingProps<T, V>) {
  const canSort = column.getCanSort();
  const sortDirection = column.getIsSorted();
  if (!canSort) {
    return <span>{children}</span>;
  }
  const handleSort = () => {
    const isDescending = column.getIsSorted() === 'asc';
    column.toggleSorting(column.getIsSorted() === 'asc');
    if (sortAnalyticsEvent) {
      trackAnalyticsEvent({
        ...sortAnalyticsEvent,
        properties: {
          column_id: column.id,
          direction: isDescending ? 'desc' : 'asc',
        },
      });
    }
  };

  return (
    <Button
      data-testid={`sort-button-${column.id}`}
      variant="ghost"
      color="secondary"
      disableRipple
      onClick={handleSort}
      className={cn(
        'flex gap-2 text-xs [&>*]:hover:opacity-100 hover:bg-inherit bg-inherit p-0 w-full',
        arrowPosition === 'left' ? 'justify-end' : 'justify-start',
      )}
    >
      {arrowPosition === 'right' && <div>{children}</div>}
      <div
        className={cn(
          'opacity-0 hover:bg-grey-20 hover:rounded-xl p-0.5',
          (sortDirection || showSortable) && 'opacity-100',
        )}
      >
        {sortDirection ? (
          <ArrowDown
            size={16}
            className={cn(
              'transition',
              sortDirection === 'asc' && 'rotate-180',
            )}
          />
        ) : (
          <ArrowDownUp className="text-grey-40" size={16} />
        )}
      </div>
      {arrowPosition === 'left' && <div>{children}</div>}
    </Button>
  );
}
